import type {
  OperationItem,
  TourOperatorRow,
  TourOperatorType,
} from '@ops/entities/operation-item';
import {
  assignableOperatorType,
  useSetTourOperators,
  useTourOperatorsData,
} from '@ops/entities/operation-item';
import { backendMessageOr } from '@ops/shared/lib/toast-mutation';
import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ServerSearchSelect,
  Tab,
  TabBar,
  Text,
  toastStore,
} from '@/shared/ui';
import css from './bulk-assign-operator-dialog.module.scss';

type BulkAssignOperatorDialogProps = {
  open: boolean;
  items: OperationItem[];
  selectedIds: Set<string>;
  onClose: () => void;
  onDone: () => void;
};

const SEARCH_LIMIT = 50;

const assignedIdsOf = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw
        .map((op) => (op as { id?: string } | null)?.id)
        .filter((id): id is string => Boolean(id))
    : [];

// Bulk driver/guide assignment: pick a type + operator, apply to every selected
// item eligible for that type via the per-item `setTourOperators` write (append
// to the item's existing operators). Items that don't accept the chosen type are
// skipped — reported in the toast.
export const BulkAssignOperatorDialog = ({
  open,
  items,
  selectedIds,
  onClose,
  onDone,
}: BulkAssignOperatorDialogProps) => {
  const [type, setType] = useState<TourOperatorType>('Driver');
  const setTourOperators = useSetTourOperators();

  const selectedItems = items.filter((item) => selectedIds.has(item.id ?? ''));
  const targetItems = selectedItems.filter(
    (item) => assignableOperatorType(item) === type
  );

  const useSearch = (query: string, isOpen: boolean) => {
    const trimmed = query.trim();
    const result = useTourOperatorsData(
      {
        search: trimmed.length > 0 ? trimmed : undefined,
        type: [type],
        offset: 0,
        limit: SEARCH_LIMIT,
      },
      { enabled: isOpen }
    );
    return { data: result.data?.data ?? [], isLoading: result.isLoading };
  };

  const handleAssign = async (operatorId: string | null) => {
    if (!operatorId || targetItems.length === 0) return;
    try {
      await Promise.all(
        targetItems.map((item) =>
          setTourOperators.mutateAsync({
            itineraryItemId: item.id ?? '',
            tourOperatorIds: [...assignedIdsOf(item.tourOperators), operatorId],
          })
        )
      );
      const skipped = selectedItems.length - targetItems.length;
      toastStore.getState().show({
        message: `${type} assigned to ${targetItems.length} item${
          targetItems.length === 1 ? '' : 's'
        }${skipped > 0 ? ` · ${skipped} skipped` : ''}`,
      });
      onDone();
      onClose();
    } catch (error) {
      // One unassignable operator fails the whole batch, so the toast must say
      // WHICH one and why — `Promise.all` rejects with the first server error,
      // whose message already names the operator and the fix.
      toastStore.getState().show({
        message: backendMessageOr(`Couldn’t assign ${type.toLowerCase()}`)(
          error as { message: string; data?: { code?: string } | null }
        ),
      });
    }
  };

  if (!open) return null;

  return (
    <Modal onClose={onClose} open={open}>
      <ModalHeader title="Assign driver / guide" />
      <div className={css.body}>
        <TabBar>
          <Tab
            active={type === 'Driver'}
            onClick={() => setType('Driver')}
            size="sm"
            type="button"
          >
            Driver
          </Tab>
          <Tab
            active={type === 'Guide'}
            onClick={() => setType('Guide')}
            size="sm"
            type="button"
          >
            Guide
          </Tab>
        </TabBar>

        <Text size="xs" variant="muted">
          {targetItems.length} of {selectedItems.length} selected item
          {selectedItems.length === 1 ? '' : 's'} accept a {type.toLowerCase()}.
        </Text>

        <ServerSearchSelect<TourOperatorRow>
          disabled={targetItems.length === 0}
          emptyMessage={`No ${type.toLowerCase()}s found`}
          getOptionLabel={(operator) => operator.fullName}
          getOptionValue={(operator) => operator.id}
          onChange={handleAssign}
          placeholder={`Search ${type.toLowerCase()}s…`}
          searchPlaceholder={`Search ${type.toLowerCase()}s…`}
          useSearch={useSearch}
          value={null}
        />
      </div>
    </Modal>
  );
};
